// Cross-context DTO pieces: document references, gate outcomes, corrections [02 §6.1, §10; MB D4].
import { z } from 'zod';

import { GATE_ID, OVERRIDE_REASON_MIN_LENGTH } from '../gates/index.ts';
import { idSchema } from '../kernel/index.ts';

/** Machine name of a document or aggregate type (snake_case, e.g. `cutting_slip`). */
export const documentTypeSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/, { message: 'invalid document type' });

/** Reference to a business document: the source of a movement, demand of a reservation, etc. */
export const documentRefSchema = z.strictObject({
  type: documentTypeSchema,
  id: idSchema,
  number: z.string().min(1).nullable(),
});

/** pass → proceed; warn → proceed and record (G9 warn mode, BR-DEV-004); block → 409 GATE_BLOCKED. */
export const gateOutcomeValues = ['pass', 'warn', 'block'] as const;

export const gateCheckSchema = z.strictObject({
  gate: GATE_ID.schema,
  outcome: z.enum(gateOutcomeValues),
  overridable: z.boolean(),
  messageKey: z.string().min(1).nullable(),
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});

/** Mandatory reason on overrides and corrections [BR-PLM-013; kernel OVERRIDE_REASON_MIN_LENGTH]. */
export const overrideReasonSchema = z.string().trim().min(OVERRIDE_REASON_MIN_LENGTH);

/** `POST /x/{id}/correct` body: whitelisted field changes + reason [02 §6.1 "Corrections after lock"]. */
export const correctionRequestSchema = z.strictObject({
  changes: z.record(z.string(), z.unknown()),
  reason: overrideReasonSchema,
});

export type DocumentRefDto = z.output<typeof documentRefSchema>;
export type GateCheckDto = z.output<typeof gateCheckSchema>;
export type CorrectionRequestDto = z.output<typeof correctionRequestSchema>;
